/**
 * Local session replay: records a Call Guidance /meet call from a local
 * `next dev` into the Kickoff Development PostHog project, with the exact
 * recording options production uses, so a replay fidelity change can be
 * checked in the real player before it ships.
 *
 * Off unless every one of these holds:
 * - `NEXT_PUBLIC_POSTHOG_LOCAL_REPLAY=1` (an explicit local opt-in);
 * - a development build (`next dev`) of a development app (`APP_ENV`);
 * - not a candidate workspace;
 * - the PostHog key is the Development project's token. Production (433067)
 *   is never a target.
 *
 * Production and staging builds are `NODE_ENV=production`, so this whole
 * mode is dead code there. How to use it:
 * documentation/explainers/call-guidance/replay-pilot.md#local-replay.
 */

/** Kickoff Development (dev and staging). */
export const DEV_POSTHOG_PROJECT_ID = 434463
/**
 * Its public ingestion token, as in next.config.js DEV_ENV and
 * node/lib/call-guidance/transcription-flag-projects.json. A `phc_` token is a
 * client key that every browser on the stage already receives.
 */
export const DEV_POSTHOG_PROJECT_TOKEN =
  'phc_oYGXG8jFKxFuF2bCqEhCZcUqJqsqUwykjqtM8LSwNNsy'

export type LocalReplayEnv = {
  optIn?: string
  appEnv?: string
  nodeEnv?: string
  candidate?: string
  key?: string
  overrides?: string
}

export type LocalReplayState =
  | 'on'
  | 'off'
  | 'not-development'
  | 'candidate'
  | 'not-dev-project'

/**
 * This build's values. Each is read by its literal name so Next inlines it
 * into the browser bundle.
 */
export const readLocalReplayEnv = (): LocalReplayEnv => ({
  optIn: process.env.NEXT_PUBLIC_POSTHOG_LOCAL_REPLAY,
  appEnv: process.env.APP_ENV,
  nodeEnv: process.env.NODE_ENV,
  candidate: process.env.KICKOFF_CANDIDATE_MODE,
  key: process.env.NEXT_PUBLIC_POSTHOG_KEY,
  overrides: process.env.NEXT_PUBLIC_POSTHOG_LOCAL_REPLAY_OPTIONS,
})

/** Whether local replay is on, or the first reason it is not. */
export const localReplayState = (
  env: LocalReplayEnv = readLocalReplayEnv()
): LocalReplayState => {
  if (env.optIn !== '1') return 'off'
  if (env.nodeEnv !== 'development' || env.appEnv !== 'development')
    return 'not-development'
  if (env.candidate === '1') return 'candidate'
  if (env.key !== DEV_POSTHOG_PROJECT_TOKEN) return 'not-dev-project'
  return 'on'
}

export const isLocalReplayOn = (env?: LocalReplayEnv): boolean =>
  localReplayState(env) === 'on'

/**
 * Session recording options to lay over the production ones for a local
 * experiment, from `NEXT_PUBLIC_POSTHOG_LOCAL_REPLAY_OPTIONS` (a JSON object,
 * for example `{"blockSelector":"video, iframe"}`). Only plain values can be
 * given this way; anything that is not a JSON object is ignored. Applied only
 * while local replay is on.
 */
export const localReplayOverrides = (
  raw: string | undefined = readLocalReplayEnv().overrides
): Record<string, unknown> => {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
      return {}
    // The masks stay production's in every local mode: an override may not
    // turn text, input or network masking off.
    return Object.fromEntries(
      Object.entries(parsed).filter(([key]) => !PROTECTED_OPTIONS.has(key))
    )
  } catch {
    return {}
  }
}

/** Recording options a local override may never replace. */
export const PROTECTED_OPTIONS = new Set([
  'maskAllInputs',
  'maskInputOptions',
  'maskInputFn',
  'maskTextFn',
  'maskTextSelector',
  'maskTextClass',
  'recordBody',
  'recordHeaders',
  'maskCapturedNetworkRequestFn',
])

/** Where a Development project recording plays. */
export const localReplayUrl = (sessionId: string) =>
  `https://us.posthog.com/project/${DEV_POSTHOG_PROJECT_ID}/replay/${encodeURIComponent(
    sessionId
  )}`
